import { Box, Stack, Typography, useTheme } from '@mui/material';
import { MouseEvent, ReactElement, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { QueryJobSiteStartPeriod } from '../../api/schema';
import Skeleton from '../../component/skeleton/Skeleton';
import {
  ACTIVITY_LEAD_PERIODS,
  ACTIVITY_PERIODS,
  AXIS,
  ActivityGranularity,
  PeriodSpan,
  TREND_PERIODS,
  buildPeriodSpans,
  formatTick,
  placeTooltip,
  trailingRate,
  yScale,
} from './periodChart';

// The Warehouse chart's initial-delivery blue: new jobs are where a job site's
// equipment starts, and the hue is already validated against the palette.
const BAR_COLOR = '#185FA5';

type JobStartsChartProps = {
  rows: QueryJobSiteStartPeriod[];
  granularity: ActivityGranularity;
  fetching: boolean;
  // Draw the trailing-average line (and list it in the legend and tooltip).
  showTrend: boolean;
  // Outer height in pixels, axis labels included.
  height?: number;
};

type Period = PeriodSpan & { jobs: number; rate: number; trend?: number };

// Average daily jobs started: one column per day, week or month, its height
// the job sites whose job started in it divided by the period's days (the
// current period by the days elapsed so far), so the three horizons share a
// scale. A dashed line is the trailing three-period average. Same inline-SVG
// construction, axis and tooltip placement as the Warehouse Activity chart.
export const JobStartsChart = ({ rows, granularity, fetching, showTrend, height = 240 }: JobStartsChartProps): ReactElement => {
  const theme = useTheme();
  const [hover, setHover] = useState<{ period: number; y: number } | undefined>();
  const daily = granularity === 'day';

  const series = useMemo(() => {
    const spans = buildPeriodSpans(granularity, ACTIVITY_PERIODS[granularity] + ACTIVITY_LEAD_PERIODS);
    const byDate = new Map(rows.map((r) => [r.date, r.jobs]));
    const totals = spans.map((span) => byDate.get(span.iso) ?? 0);
    const all: Period[] = spans.map((span, i) => ({ ...span, jobs: totals[i], rate: totals[i] / span.days, trend: trailingRate(totals, spans, i) }));
    const periods = all.slice(ACTIVITY_LEAD_PERIODS);
    // A hidden trend does not stretch the axis.
    const tallest = Math.max(0, ...periods.flatMap((p) => (showTrend ? [p.rate, p.trend ?? 0] : [p.rate])));
    return { periods, ...yScale(tallest, daily) };
  }, [rows, granularity, daily, showTrend]);

  // Whole jobs for a day; two decimals for a rate over several days (a few
  // jobs a week is a fraction of one a day).
  const formatRate = (value: number) => (daily ? String(Math.round(value)) : value.toFixed(2));

  const periodWidth = 100 / Math.max(1, series.periods.length);
  const barWidth = periodWidth * 0.5;
  const barX = (pi: number) => pi * periodWidth + (periodWidth - barWidth) / 2;
  const y = (value: number) => 100 - (value / series.max) * 100;

  const plotRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [tipSize, setTipSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (tipRef.current) {
      const r = tipRef.current.getBoundingClientRect();
      if (r.width !== tipSize.w || r.height !== tipSize.h) setTipSize({ w: r.width, h: r.height });
    }
  }, [hover?.period, tipSize.w, tipSize.h]);

  // The period under the cursor comes from its x position, so the gaps between
  // columns still read a period.
  const onMove = (event: MouseEvent<HTMLDivElement>) => {
    const box = plotRef.current?.getBoundingClientRect();
    if (!box || !series.periods.length) return;
    const x = event.clientX - box.left;
    const period = Math.min(series.periods.length - 1, Math.max(0, Math.floor((x / box.width) * series.periods.length)));
    setHover({ period, y: event.clientY - box.top });
  };

  const tipPosition = () => {
    const box = plotRef.current?.getBoundingClientRect();
    if (!hover || !box) return { left: 0, top: 0 };
    return placeTooltip(box, hover.period, series.periods.length, hover.y, tipSize);
  };

  const surface = theme.palette.background.paper;
  const hovered = hover ? series.periods[hover.period] : undefined;
  const trendLabel = `${TREND_PERIODS}-${granularity} avg`;

  const cellSx = { fontSize: 12, fontWeight: 500, color: 'text.primary', textAlign: 'right' } as const;
  const nameSx = { ...cellSx, textAlign: 'left', pr: 1 } as const;

  return (
    <Box>
      {/* legend only when the trend adds a second mark; the title names the bars */}
      {showTrend && (
        <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ mb: 1.5, columnGap: '14px', rowGap: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: BAR_COLOR }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
              New jobs
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 14, height: 0, borderTop: `2px dashed ${BAR_COLOR}` }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
              {trendLabel}
            </Typography>
          </Stack>
        </Stack>
      )}
      {fetching ? (
        <Skeleton fetching height={height - 40} />
      ) : (
        <Box sx={{ position: 'relative', height, pl: `${AXIS.left}px`, pb: `${AXIS.bottom}px`, pt: `${AXIS.top}px`, pr: `${AXIS.right}px` }}>
          <Box ref={plotRef} sx={{ position: 'relative', height: '100%' }} onMouseMove={onMove} onMouseLeave={() => setHover(undefined)}>
            {/* y axis */}
            {series.ticks.map((v) => (
              <Typography
                key={v}
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  left: -AXIS.left,
                  width: AXIS.left - 6,
                  textAlign: 'right',
                  top: `${y(v)}%`,
                  transform: 'translateY(-50%)',
                  fontSize: 11,
                  lineHeight: 1,
                }}
              >
                {formatTick(v, series.step)}
              </Typography>
            ))}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
              {series.ticks.map((v) => (
                <line key={v} x1={0} x2={100} y1={y(v)} y2={y(v)} stroke={theme.palette.grey[200]} strokeWidth={1} vectorEffect="non-scaling-stroke" />
              ))}
              {series.periods.map((p, pi) =>
                p.rate ? (
                  <rect
                    key={p.iso}
                    x={barX(pi)}
                    y={y(p.rate)}
                    width={barWidth}
                    height={100 - y(p.rate)}
                    fill={BAR_COLOR}
                    fillOpacity={hover && hover.period !== pi ? 0.55 : 1}
                  />
                ) : null,
              )}
              {/* trend line: a surface halo under it so it reads over the bars */}
              {showTrend &&
                (() => {
                  const points = series.periods.map((p, pi) => `${barX(pi) + barWidth / 2},${y(p.trend ?? 0)}`).join(' ');
                  return (
                    <g pointerEvents="none">
                      <polyline points={points} fill="none" stroke={surface} strokeWidth={5} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                      <polyline
                        points={points}
                        fill="none"
                        stroke={BAR_COLOR}
                        strokeWidth={2}
                        strokeDasharray="5 3"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                      />
                    </g>
                  );
                })()}
            </svg>
            {/* x axis: every period labelled */}
            {series.periods.map((p, pi) => (
              <Typography
                key={p.iso}
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: `${pi * periodWidth + periodWidth / 2}%`,
                  transform: 'translateX(-50%)',
                  mt: 0.75,
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                }}
              >
                {p.label}
              </Typography>
            ))}
          </Box>
          {hover && hovered && (
            <Box
              ref={tipRef}
              sx={{
                position: 'absolute',
                ...tipPosition(),
                zIndex: 2,
                pointerEvents: 'none',
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                boxShadow: '0 2px 8px rgba(0,0,0,.08)',
                borderRadius: 2,
                px: '12px',
                py: '10px',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                width: 'max-content',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>
                {hovered.title}
                {hovered.partial ? ` · ${hovered.days} ${hovered.days === 1 ? 'day' : 'days'} so far` : ''}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto max-content auto', columnGap: 1.5, rowGap: 0.25, alignItems: 'center' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: BAR_COLOR }} />
                <Typography sx={nameSx}>New jobs</Typography>
                <Typography sx={cellSx}>{hovered.jobs}</Typography>
                {!daily && [
                  <Box key="rate-swatch" />,
                  <Typography key="rate-name" sx={nameSx}>
                    Per day
                  </Typography>,
                  <Typography key="rate-value" sx={cellSx}>
                    {formatRate(hovered.rate)}
                  </Typography>,
                ]}
                {showTrend && [
                  <Box key="trend-swatch" sx={{ width: 10, height: 0, borderTop: `2px dashed ${BAR_COLOR}` }} />,
                  <Typography key="trend-name" sx={{ ...nameSx, color: 'text.secondary', fontWeight: 400 }}>
                    {trendLabel} / day
                  </Typography>,
                  <Typography key="trend-value" sx={{ ...cellSx, color: 'text.secondary', fontWeight: 400 }}>
                    {(hovered.trend ?? 0).toFixed(2)}
                  </Typography>,
                ]}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
